import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { GameFrame, pick, shuffle, useGame, useRoundTimer, type GameProps } from '@el/sdk';
import { isShape, tracePrompt, type TraceGuide } from '@el/blocks/phrases';

type Point = { x: number; y: number };
type Guide = TraceGuide;

const STRICT: Record<string, number> = { gentle: 0.55, normal: 0.7, strict: 0.82 };
const W = 640;
const H = 300;
const SCALE = 4;

function drawShape(ctx: CanvasRenderingContext2D, name: string, box: { x: number; y: number; w: number; h: number }) {
  const { x, y, w, h } = box;
  const cx = x + w / 2, cy = y + h / 2, r = Math.min(w, h) / 2;
  ctx.beginPath();
  switch (name) {
    case 'circle': ctx.arc(cx, cy, r, 0, Math.PI * 2); break;
    case 'square': ctx.rect(cx - r, cy - r, r * 2, r * 2); break;
    case 'triangle': ctx.moveTo(cx, cy - r); ctx.lineTo(cx + r, cy + r); ctx.lineTo(cx - r, cy + r); ctx.closePath(); break;
    case 'line': ctx.moveTo(x, cy); ctx.lineTo(x + w, cy); break;
    case 'cross': ctx.moveTo(cx - r, cy - r); ctx.lineTo(cx + r, cy + r); ctx.moveTo(cx + r, cy - r); ctx.lineTo(cx - r, cy + r); break;
    case 'zigzag': for (let i = 0; i <= 6; i++) { const px = x + (w * i) / 6, py = i % 2 ? y + h * 0.2 : y + h * 0.8; if (i) ctx.lineTo(px, py); else ctx.moveTo(px, py); } break;
    default: for (let i = 0; i <= 60; i++) { const px = x + (w * i) / 60, py = cy + Math.sin((i / 60) * Math.PI * 4) * h * 0.3; if (i) ctx.lineTo(px, py); else ctx.moveTo(px, py); }
  }
  ctx.stroke();
}

function renderGuide(ctx: CanvasRenderingContext2D, target: string, font: string, stroke: number, extra = 0) {
  const t = target.trim();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (isShape(t)) {
    ctx.lineWidth = stroke + extra;
    drawShape(ctx, t.toLowerCase(), { x: W * 0.2, y: H * 0.12, w: W * 0.6, h: H * 0.76 });
    return;
  }
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = `700 100px ${font}`;
  const m = ctx.measureText(t);
  const gw = m.actualBoundingBoxLeft + m.actualBoundingBoxRight;
  const gh = m.actualBoundingBoxAscent + m.actualBoundingBoxDescent;
  const size = 100 * Math.min((H * 0.75) / Math.max(1, gh), (W * 0.9) / Math.max(1, gw));
  ctx.font = `700 ${size}px ${font}`;
  const k = size / 100;
  const x = W / 2 - (gw * k) / 2 + m.actualBoundingBoxLeft * k;
  const y = H / 2 - (gh * k) / 2 + m.actualBoundingBoxAscent * k;
  ctx.fillText(t, x, y);
  if (extra) { ctx.lineWidth = extra; ctx.strokeText(t, x, y); }
}

function mask(draw: (ctx: CanvasRenderingContext2D) => void): Uint8Array {
  const c = document.createElement('canvas');
  c.width = W / SCALE;
  c.height = H / SCALE;
  const ctx = c.getContext('2d')!;
  ctx.scale(1 / SCALE, 1 / SCALE);
  ctx.fillStyle = '#000';
  ctx.strokeStyle = '#000';
  draw(ctx);
  const d = ctx.getImageData(0, 0, c.width, c.height).data;
  const out = new Uint8Array(c.width * c.height);
  for (let i = 0; i < out.length; i++) out[i] = d[i * 4 + 3] > 40 ? 1 : 0;
  return out;
}

function strokesTo(ctx: CanvasRenderingContext2D, strokes: Point[][], width: number, map: (p: Point) => Point = (p) => p) {
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = width;
  for (const s of strokes) {
    ctx.beginPath();
    s.forEach((p, i) => { const q = map(p); if (i) ctx.lineTo(q.x, q.y); else ctx.moveTo(q.x, q.y); });
    if (s.length === 1) { const q = map(s[0]); ctx.lineTo(q.x + 0.1, q.y); }
    ctx.stroke();
  }
}

function judge(target: string, strokes: Point[][], guide: Guide, font: string, stroke: number): number {
  if (!strokes.some((s) => s.length)) return 0;
  let map = (p: Point) => p;
  if (guide === 'copy') {
    const g = mask((ctx) => renderGuide(ctx, target, font, stroke));
    const gw = W / SCALE;
    let gx0 = Infinity, gy0 = Infinity, gx1 = -1, gy1 = -1;
    g.forEach((v, i) => { if (v) { const x = i % gw, y = Math.floor(i / gw); gx0 = Math.min(gx0, x); gx1 = Math.max(gx1, x); gy0 = Math.min(gy0, y); gy1 = Math.max(gy1, y); } });
    const all = strokes.flat();
    const ix0 = Math.min(...all.map((p) => p.x)), ix1 = Math.max(...all.map((p) => p.x));
    const iy0 = Math.min(...all.map((p) => p.y)), iy1 = Math.max(...all.map((p) => p.y));
    const sx = ((gx1 - gx0 + 1) * SCALE) / Math.max(1, ix1 - ix0), sy = ((gy1 - gy0 + 1) * SCALE) / Math.max(1, iy1 - iy0);
    map = (p) => ({ x: gx0 * SCALE + (p.x - ix0) * sx, y: gy0 * SCALE + (p.y - iy0) * sy });
  }
  const guideMask = mask((ctx) => renderGuide(ctx, target, font, stroke));
  const guideWide = mask((ctx) => renderGuide(ctx, target, font, stroke, stroke * 1.6));
  const inkMask = mask((ctx) => strokesTo(ctx, strokes, stroke, map));
  const inkWide = mask((ctx) => strokesTo(ctx, strokes, stroke * 2.6, map));
  let g = 0, gCovered = 0, ink = 0, inkOn = 0;
  for (let i = 0; i < guideMask.length; i++) {
    if (guideMask[i]) { g++; if (inkWide[i]) gCovered++; }
    if (inkMask[i]) { ink++; if (guideWide[i]) inkOn++; }
  }
  if (!g || !ink) return 0;
  return Math.min(gCovered / g, inkOn / ink);
}

const promptFor = tracePrompt;

export default function TraceIt(props: GameProps) {
  const game = useGame(props);
  const targets = ((game.config?.prompts ?? []) as { prompt: string }[]).map((p) => String(p?.prompt ?? '').trim()).filter(Boolean);
  const guide = game.str('guide', 'trace') as Guide;
  const need = STRICT[game.str('strictness', 'normal')] ?? STRICT.normal;
  const fixed = game.str('trialOrder', 'random') === 'fixed';
  const [target, setTarget] = useState<string | null>(null);
  const [trial, setTrial] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [score, setScore] = useState(0);
  const [inked, setInked] = useState(false);
  const strokes = useRef<Point[][]>([]);
  const drawing = useRef(false);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const model = useRef<HTMLCanvasElement | null>(null);
  const queue = useRef<string[]>([]);
  const busy = useRef(false);
  const font = typeof document === 'undefined' ? 'sans-serif' : getComputedStyle(document.body).fontFamily;
  const stroke = Math.round(H * 0.07);

  const paint = useCallback(() => {
    const c = canvas.current;
    if (!c || !target) return;
    const ctx = c.getContext('2d')!;
    const cs = getComputedStyle(c);
    ctx.clearRect(0, 0, W, H);
    if (guide !== 'copy') {
      ctx.save();
      ctx.globalAlpha = guide === 'faint' ? 0.14 : 0.32;
      ctx.fillStyle = cs.getPropertyValue('--ink-soft') || '#666';
      ctx.strokeStyle = ctx.fillStyle;
      renderGuide(ctx, target, font, stroke);
      ctx.restore();
    }
    ctx.strokeStyle = done ? (cs.getPropertyValue('--success') || '#2f7d4f') : (cs.getPropertyValue('--ink') || '#222');
    strokesTo(ctx, strokes.current, stroke * 0.7);
    const m = model.current?.getContext('2d');
    if (m) {
      m.clearRect(0, 0, W, H);
      m.fillStyle = cs.getPropertyValue('--ink') || '#222';
      m.strokeStyle = m.fillStyle;
      renderGuide(m, target, font, stroke);
    }
  }, [target, guide, done, font, stroke]);

  useEffect(() => { paint(); }, [paint]);

  const run = useRef(0);

  const start = useCallback(async () => {
    if (targets.length === 0) return;
    const mine = ++run.current;
    if (queue.current.length === 0) queue.current = fixed ? [...targets] : shuffle(targets);
    const next = queue.current[0] ?? pick(targets);
    strokes.current = [];
    setInked(false);
    setScore(0);
    setDone(false);
    const id = await game.register({ target: next, guide }, { value: 'traced' });
    if (!id || mine !== run.current) return;
    queue.current.shift();
    setTarget(next);
    setTrial(id);
  }, [targets.join('|'), fixed, guide]);

  useEffect(() => { if (game.config) void start(); }, [game.config, start]);
  useEffect(() => {
    if (!target || game.textPrompt) return;
    const t = window.setTimeout(() => game.say(promptFor(target, guide)), 300);
    return () => window.clearTimeout(t);
  }, [target, trial]);

  const at = (e: ReactPointerEvent<HTMLCanvasElement>): Point => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * W, y: ((e.clientY - r.top) / r.height) * H };
  };

  function down(e: ReactPointerEvent<HTMLCanvasElement>) {
    if (done || !target) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    strokes.current.push([at(e)]);
    setInked(true);
    paint();
  }

  function move(e: ReactPointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    strokes.current[strokes.current.length - 1].push(at(e));
    paint();
  }

  function up() {
    if (!drawing.current || !target) return;
    drawing.current = false;
    setScore(judge(target, strokes.current, guide, font, stroke));
  }

  function clear() {
    strokes.current = [];
    setInked(false);
    setScore(0);
    paint();
  }

  async function finish() {
    if (!target || !trial || done || busy.current) return;
    const passed = score >= need;
    if (game.errorless && !passed) return;
    busy.current = true;
    const v = await game.respond(trial, passed ? 'traced' : 'not yet');
    if (v?.correct) { setDone(true); busy.current = false; return; }
    clear();
    if (!game.textPrompt) game.say(promptFor(target, guide));
    const fresh = await game.register({ target, guide, retry: true }, { value: 'traced' });
    busy.current = false;
    if (fresh) setTrial(fresh);
  }

  const fading = useRoundTimer(done, game.transitionMs, () => { void start(); });
  const ready = inked && (!game.errorless || score >= need);

  return (
    <GameFrame game={game} empty={targets.length === 0} emptyText="The learner traces or writes a letter, number, word or shape with a finger or pen. Edit it to add letters, numbers, words or shapes." onResume={() => { void start(); }} breakTrial={trial}>
      {target && (
        <div className={`g-board g-trace${fading ? ' is-fading' : ''}`}>
          <div className="g-prompt" aria-live="polite">
            <span className="g-prompt-text">{promptFor(target, guide)}</span>
            {!game.textPrompt && (
              <button type="button" className="g-say" aria-label="Say it again" onClick={() => game.say(promptFor(target, guide))}><span aria-hidden="true">🔊</span></button>
            )}
          </div>
          {guide === 'copy' && (
            <canvas ref={model} className="g-trace-model" width={W} height={H} role="img" aria-label={`Example: ${target}`} />
          )}
          <canvas
            ref={canvas}
            className={`g-trace-pad${done ? ' correct' : ''}`}
            width={W}
            height={H}
            role="img"
            aria-label={`Writing space for ${target}`}
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
          />
          <div className="g-trace-actions">
            <button type="button" className="btn secondary" disabled={!inked || done} onClick={clear}>Start again</button>
            <button type="button" className="btn" disabled={!ready || done} onClick={() => void finish()}>Finished</button>
          </div>
        </div>
      )}
    </GameFrame>
  );
}
